import {
  AnimationClip,
  AnimationMixer,
  Euler,
  LoopOnce,
  Object3D,
  Quaternion,
  QuaternionKeyframeTrack,
  Vector3,
  VectorKeyframeTrack,
} from "three";

/** Bake the sandbox's rest-axis correction into clips BEFORE the mixer blends them.
 * Applying it after a crossfade interprets the target rest pose as source motion.
 * Animation-only GLBs contain named Object3Ds, not necessarily Bone instances.
 * The sandbox fallback already uses compatible upper-body channels; preserve
 * those and adapt the lower-body rest axes that differ on the generated rig.
 */
export function retargetSandboxClip(
  source: Object3D,
  target: Object3D,
  clip: AnimationClip,
): AnimationClip {
  const sourceRest = new Map<string, Quaternion>();
  const targetRest = new Map<string, Quaternion>();
  for (const [root, rest] of [
    [source, sourceRest],
    [target, targetRest],
  ] as const) {
    root.traverse((node) => {
      if (node.name) rest.set(node.name, node.quaternion.clone().normalize());
    });
  }
  source.updateWorldMatrix(true, true);
  target.updateWorldMatrix(true, true);
  const basis = new Map<string, Quaternion>();
  target.traverse((node) => {
    const sourceNode = source.getObjectByName(node.name);
    if (sourceNode)
      basis.set(
        node.name,
        node
          .getWorldQuaternion(new Quaternion())
          .invert()
          .multiply(sourceNode.getWorldQuaternion(new Quaternion())),
      );
  });
  const tracks = clip.tracks
    .filter((track) => track.name.endsWith(".quaternion"))
    .map((track) => {
      const name = track.name.slice(0, -".quaternion".length);
      const from = sourceRest.get(name),
        to = targetRest.get(name);
      if (!from || !to || !/^(hips|.*leg|.*foot|.*toebase)$/i.test(name))
        return track.clone();
      const sourceKnee = /^(Left|Right)Leg$/.test(name)
        ? source.getObjectByName(name)
        : undefined;
      const targetKnee = sourceKnee ? target.getObjectByName(name) : undefined;
      const sourceFoot = sourceKnee?.getObjectByName(
        name.replace("Leg", "Foot"),
      );
      const targetFoot = targetKnee?.getObjectByName(
        name.replace("Leg", "Foot"),
      );
      let knee:
        | {
            sourceThigh: Vector3;
            sourceShin: Vector3;
            straight: Quaternion;
            axis: Vector3;
          }
        | undefined;
      if (sourceKnee && targetKnee?.parent && sourceFoot && targetFoot) {
        const thigh = targetKnee.position.clone().normalize();
        const restShin = targetFoot.position
          .clone()
          .normalize()
          .applyQuaternion(to);
        const straight = new Quaternion()
          .setFromUnitVectors(restShin, thigh)
          .multiply(to);
        const back = new Vector3(0, 0, -1)
          .applyQuaternion(target.getWorldQuaternion(new Quaternion()))
          .applyQuaternion(
            targetKnee.parent.getWorldQuaternion(new Quaternion()).invert(),
          );
        const axis = thigh.clone().cross(back).normalize();
        if (axis.lengthSq() > 0.5)
          knee = {
            sourceThigh: sourceKnee.position.clone().normalize(),
            sourceShin: sourceFoot.position.clone().normalize(),
            straight,
            axis,
          };
      }
      const values = new Float32Array(track.values.length);
      const q = new Quaternion(),
        previous = new Quaternion();
      const axes = basis.get(name) || new Quaternion();
      const sourceInverse = from.clone().invert();
      const axesInverse = axes.clone().invert();
      for (let i = 0; i < values.length; i += 4) {
        // Express the source rotation delta in the target bone axes.
        // Copying the delta directly reverses knee flexion on opposite-axis rigs.
        q.fromArray(track.values, i).premultiply(sourceInverse);
        q.premultiply(axes).multiply(axesInverse).premultiply(to).normalize();
        if (knee) {
          // Use geometric flexion magnitude, independent of exported knee axes.
          const sourceShin = knee.sourceShin
            .clone()
            .applyQuaternion(
              new Quaternion().fromArray(track.values, i).normalize(),
            );
          const flexion = Math.acos(
            Math.max(-1, Math.min(1, knee.sourceThigh.dot(sourceShin))),
          );
          q.setFromAxisAngle(knee.axis, Math.min(Math.PI * 0.85, flexion))
            .multiply(knee.straight)
            .normalize();
        }
        if (i && q.dot(previous) < 0) q.set(-q.x, -q.y, -q.z, -q.w);
        q.toArray(values, i);
        previous.copy(q);
      }
      return new QuaternionKeyframeTrack(
        track.name,
        track.times.slice(),
        values,
        track.getInterpolation(),
      );
    });
  return new AnimationClip(clip.name, clip.duration, tracks);
}

/** Bake a level locomotion posture; preserve limb motion and authored rest axes. */
export function stabilizeLocomotionClip(
  target: Object3D,
  clip: AnimationClip,
): AnimationClip {
  const rig = target.clone(true);
  rig.updateWorldMatrix(true, true);
  const nodes = new Map<string, Object3D>();
  const rest = new Map<string, Quaternion>();
  rig.traverse((node) => {
    if (node.name) {
      nodes.set(node.name, node);
      rest.set(node.name, node.getWorldQuaternion(new Quaternion()));
    }
  });
  const names = new Set(
    clip.tracks
      .filter((t) => t.name.endsWith(".quaternion"))
      .map((t) => t.name.slice(0, -11))
      .filter((n) => nodes.has(n)),
  );
  const posture = [
    "Hips",
    "Spine",
    "Spine1",
    "Spine2",
    "Spine01",
    "Spine02",
    "Neck",
    "neck",
    "Head",
  ].filter((n) => nodes.has(n));
  const depth = (name: string) => {
    let d = 0;
    let p = nodes.get(name)?.parent;
    while (p) {
      d++;
      p = p.parent;
    }
    return d;
  };
  posture.sort((a, b) => depth(a) - depth(b));
  posture.forEach((n) => names.add(n));
  const mixer = new AnimationMixer(rig);
  const sampled = clip.clone();
  sampled.tracks = sampled.tracks.filter(
    (t) => t.name.endsWith(".quaternion") && names.has(t.name.slice(0, -11)),
  );
  const action = mixer.clipAction(sampled).setLoop(LoopOnce, 1);
  action.clampWhenFinished = true;
  action.play();
  const count = Math.max(2, Math.ceil(clip.duration * 60) + 1);
  const times = new Float32Array(count);
  const output = new Map(
    [...names].map((n) => [n, new Float32Array(count * 4)]),
  );
  const rootQ = rig.getWorldQuaternion(new Quaternion());
  const rootInverse = rootQ.clone().invert();
  const hips = nodes.get("Hips");
  let runHipPosition: Vector3 | undefined;
  if (clip.name === "run" && hips?.parent) {
    const local = rig.worldToLocal(hips.getWorldPosition(new Vector3()));
    local.z -= 0.035;
    runHipPosition = hips.parent.worldToLocal(rig.localToWorld(local));
  }
  for (let i = 0; i < count; i++) {
    const time = (i * clip.duration) / (count - 1);
    times[i] = time;
    mixer.setTime(time);
    rig.updateWorldMatrix(true, true);
    if (clip.name === "run")
      for (const side of ["Left", "Right"]) {
        const thigh = nodes.get(side + "UpLeg"),
          knee = nodes.get(side + "Leg");
        if (!thigh?.parent || !knee) continue;
        const parentRest = rest.get(thigh.parent.name) || rootQ;
        const localRest = parentRest
          .clone()
          .invert()
          .multiply(rest.get(thigh.name)!);
        const down = knee.position
          .clone()
          .normalize()
          .applyQuaternion(localRest);
        const forward = new Vector3(0, 0, 1)
          .applyQuaternion(rootQ)
          .applyQuaternion(parentRest.clone().invert());
        forward.addScaledVector(down, -forward.dot(down)).normalize();
        const animated = knee.position
          .clone()
          .normalize()
          .applyQuaternion(thigh.quaternion);
        const angle = Math.max(
          -1.2,
          Math.min(1.2, Math.atan2(animated.dot(forward), animated.dot(down))),
        );
        const direction = down
          .clone()
          .multiplyScalar(Math.cos(angle))
          .addScaledVector(forward, Math.sin(angle))
          .normalize();
        thigh.quaternion.copy(
          new Quaternion()
            .setFromUnitVectors(down, direction)
            .multiply(localRest),
        );
        thigh.updateWorldMatrix(false, true);
      }
    for (const name of posture) {
      const bone = nodes.get(name)!;
      const neutral = rest.get(name)!;
      const desired = neutral.clone();
      if (!/^(Head|Neck|neck)$/.test(name)) {
        const delta = bone
          .getWorldQuaternion(new Quaternion())
          .multiply(neutral.clone().invert());
        delta.premultiply(rootInverse).multiply(rootQ);
        const e = new Euler().setFromQuaternion(delta, "YXZ");
        // Mild forward motion, bounded side sway, and no authored turning arc.
        e.x =
          clip.name === "run"
            ? Math.max(0.12, Math.min(0.2, e.x + 0.08))
            : Math.max(-0.04, Math.min(0.14, e.x));
        e.y = Math.max(-0.06, Math.min(0.06, e.y));
        e.z = Math.max(-0.035, Math.min(0.035, e.z));
        desired.premultiply(
          rootQ
            .clone()
            .multiply(new Quaternion().setFromEuler(e))
            .multiply(rootInverse),
        );
      }
      const parent =
        bone.parent?.getWorldQuaternion(new Quaternion()) || new Quaternion();
      bone.quaternion.copy(parent.invert().multiply(desired));
      bone.updateWorldMatrix(false, true);
    }
    for (const name of names)
      nodes.get(name)!.quaternion.toArray(output.get(name)!, i * 4);
  }
  mixer.stopAllAction();
  mixer.uncacheRoot(rig);
  const result = new AnimationClip(
    clip.name,
    clip.duration,
    [...names].map(
      (name) =>
        new QuaternionKeyframeTrack(
          `${name}.quaternion`,
          times,
          output.get(name)!,
        ),
    ),
  );
  if (runHipPosition)
    result.tracks.push(
      new VectorKeyframeTrack(
        "Hips.position",
        [0, clip.duration],
        [...runHipPosition.toArray(), ...runHipPosition.toArray()],
      ),
    );
  return result;
}

/** Reject embedded reference-pose clips when selecting a standing idle. */
export function hasAnimatedPose(clip: AnimationClip): boolean {
  return clip.tracks.some((track) => {
    if (!track.name.endsWith(".quaternion")) return false;
    const first = new Quaternion().fromArray(track.values, 0).normalize();
    const sample = new Quaternion();
    for (let i = 4; i < track.values.length; i += 4) {
      if (first.angleTo(sample.fromArray(track.values, i).normalize()) > 0.025)
        return true;
    }
    return false;
  });
}
